import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CreditCard, Smartphone, Landmark, Coins } from "lucide-react";
import { api, formatApiError, inrPlain } from "@/lib/api";

const MODES = [
  { key: "UPI", label: "UPI", icon: Smartphone },
  { key: "Cash", label: "Cash", icon: Coins },
  { key: "Bank", label: "Bank", icon: Landmark },
  { key: "Card", label: "Card", icon: CreditCard },
];

export default function SettlementDialog({ open, onOpenChange, groupId, payerId, receiverId, outstanding, onSuccess }) {
  const [amount, setAmount] = useState(outstanding || 0);
  const [mode, setMode] = useState("UPI");
  const [ref, setRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setAmount(outstanding || 0);
      setMode("UPI");
      setRef("");
      setRemarks("");
    }
  }, [open, outstanding]);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSaving(true);
    try {
      await api.post("/settlements", {
        group_id: groupId,
        payer_id: payerId,
        receiver_id: receiverId,
        amount: Number(amount),
        mode,
        reference_number: ref,
        remarks,
      });
      toast.success("Settlement recorded");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="settlement-dialog">
        <DialogHeader>
          <DialogTitle className="heading text-2xl">Record settlement</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Outstanding</div>
            <div className="amount text-3xl font-bold text-slate-900 mt-1">{inrPlain(outstanding)}</div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Amount to settle</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-2 h-14 text-2xl amount font-semibold rounded-2xl"
              data-testid="settlement-amount-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Payment mode</Label>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {MODES.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMode(key)}
                  data-testid={`mode-${key.toLowerCase()}`}
                  className={`p-3 rounded-2xl border transition-all active:scale-95 ${
                    mode === key
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <Icon className="w-4 h-4 mx-auto" />
                  <div className="text-[11px] font-semibold mt-1">{label}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Reference number (optional)</Label>
            <Input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="Transaction ID"
              className="mt-2 h-12 rounded-2xl"
              data-testid="settlement-ref-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Remarks</Label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add a note"
              className="mt-2 rounded-2xl min-h-[70px]"
              data-testid="settlement-remarks-input"
            />
          </div>

          <Button
            disabled={saving}
            onClick={submit}
            className="w-full h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold"
            data-testid="settlement-confirm-button"
          >
            {saving ? "Recording…" : "Confirm settlement"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
